/**
 * buildStreamHandlers · 构建 SSE 流式回调
 *
 * 将 useAIClassifySession 中 streamChat 的回调定义集中到此处，
 * 让 effect 只关注"何时发起流"，不关注"流事件怎么处理"。
 * @module buildStreamHandlers
 */

import type { StreamHandlers } from './aiClassifySse'
import type { AssistantPayload } from '../../../types/ai-classify'
import {
  assistantDone,
  assistantErrored,
  assistantFoldersHydrated,
  assistantPartialFinal,
  assistantPlanSnapshotted,
  assistantToolEnded,
  assistantToolStarted,
  conversationReplaced,
  setClassifyPreview,
  sessionStatusUpdated,
  streamFinished,
  streamErrored
} from '../../../slices/aiClassify'
import { fetchAiChatHistoryFolders } from './aiChatHistoryFolders'
import { previewFoldersToPlanFolders } from './aiChatHistoryFolders'
import { trackAIChatClick } from '../utils/trackClassify'

type Dispatch = (action: unknown) => void

export interface StreamHandlersDeps {
  /** Redux dispatch */
  dispatch: Dispatch
  /** 本轮 assistant 消息 ID */
  assistantId: string
  /** 用户发送的原始文本（用于埋点） */
  userText: string
  /** 团队 UUID（用于刷新建夹任务列表） */
  groupID: string
}

/**
 * 根据当前轮次的上下文，构建 streamChat 所需的全部回调。
 *
 * @param deps - 本轮 SSE 所需的上下文依赖
 * @returns StreamHandlers 对象，直接传给 streamChat 第二个参数
 */
export function buildStreamHandlers(deps: StreamHandlersDeps): StreamHandlers {
  const { dispatch, assistantId, userText, groupID } = deps

  return {
    onDelta: () => {
      /* streaming 阶段不展示 raw delta，done 事件才有干净人话 */
    },

    onDone: (e) => {
      trackAIChatClick('conversation', {
        string1: userText,
        string2: e.message || ''
      })
      // 后端在 done 事件透传本次的 conversation_id · 用户在新对话欢迎页发首条时
      // 后端落库新会话, 这里把新 id 回写 redux。
      //
      // 此处无脑 dispatch, 不做"本轮发起时是否新会话"的快照判断 — 因为闭包持有的
      // 是发起时的 conversationId 快照, 用户可能在 SSE 进行中切换了会话, 快照已过期。
      // 由 reducer (conversationReplaced) 内部严格守卫: 仅在 redux.conversationId
      // 仍为 null 时接受回填, 防止废弃 SSE 把当前活跃会话的 id 污染掉。
      // 守卫逻辑详见 sessionSlice.ts 的 conversationReplaced reducer 注释。
      if (e.conversationId) {
        dispatch(conversationReplaced(e.conversationId))
      }
      const payload: AssistantPayload = {
        action: e.action,
        card: e.card,
        bottomOptions: e.bottomOptions,
        toolCalls: e.toolCalls,
        classifyState: e.classifyState
      }
      dispatch(
        assistantDone({
          messageId: assistantId,
          ...(e.message ? { finalText: e.message } : {}),
          payload,
          backendMessageId: e.backendMessageId
        })
      )
      dispatch(streamFinished())

      // live 路径 hydrate folders · 跟 history 同构
      //
      // streaming 期间 assistantPlanSnapshotted 只写浅字段 (sessionId/chainVersion/
      // layersSummary 等), 文件夹树 / 反选 / 任务态由本步骤 hydrate 补齐。
      //
      // 时机: SSE done 后假定 backend 同步落库, 用 backendMessageId 调接口 5。
      // (跟 conversationRestore.hydrateFolderMessages 同款接口, 同款回填 reducer
      // assistantFoldersHydrated · 真正实现 "live/history 同构"。)
      //
      // 守卫:
      //   · 仅 folder_plan kind 的 card 才需要 hydrate (e.card 是 ask/next_level
      //     等其他类型时跳过, 避免对非方案卡发起接口请求)
      //   · backendMessageId 缺失 (协议异常) 跳过
      //   · 失败时 console.warn, 不影响其他流程 (用户视角: 方案卡显示 loading
      //     占位, 跟 history 拉接口失败的退化路径一致)
      if (
        e.backendMessageId &&
        groupID &&
        (e.card?.kind === 'folder_plan' || !e.card)
        // !e.card 兜底: v6 纯 dimension_op 路径下 done 不带 card, 但 streaming
        // 期间 assistantPlanSnapshotted 已经写入了 folder_plan, 仍需 hydrate
      ) {
        void fetchAiChatHistoryFolders(e.backendMessageId, groupID)
          .then((resp) => {
            const folders = previewFoldersToPlanFolders(
              Array.isArray(resp?.folders) ? resp.folders : []
            )
            const excludedKeys = Array.isArray(resp?.excluded_folder_keys)
              ? resp.excluded_folder_keys
              : []
            const taskCreated = resp?.task_created === true
            dispatch(
              assistantFoldersHydrated({
                messageId: assistantId,
                folders,
                excludedKeys,
                taskCreated
              })
            )
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.warn(
              '[ai-classify] live hydrate folders failed:',
              e.backendMessageId,
              err
            )
          })
      }
    },

    onError: (err) => {
      dispatch(assistantErrored({ messageId: assistantId, error: err }))
      dispatch(streamErrored({ code: 'STREAM_ERR', message: err }))
    },

    onToolStart: (record) => {
      dispatch(assistantToolStarted({ messageId: assistantId, record }))
    },

    onToolEnd: (record) => {
      dispatch(assistantToolEnded({ messageId: assistantId, record }))
    },

    onFinalPartial: (e) => {
      dispatch(
        assistantPartialFinal({
          messageId: assistantId,
          action: e.action,
          card: e.card,
          classifyState: e.classifyState
        })
      )
      if (e.sessionStatus && e.sessionId) {
        dispatch(
          sessionStatusUpdated({
            sessionId: e.sessionId,
            status: e.sessionStatus
          })
        )
      }
    },

    onDimensionUpdated: (e) => {
      // dimension_updated → 两条 dispatch:
      //   1. setClassifyPreview · 刷新 selection 当前活跃方案句柄 (sessionId
      //      / chainVersion / layersSummary), 给 useConfirmCreate 等需要 session
      //      上下文的路径用
      //   2. assistantPlanSnapshotted · 把维度链摘要快照到当前 streaming
      //      assistant message 的 payload.card · 让 live 多轮 refine 每条
      //      message 都持有自己的方案预览 (跟 history 同构, 取代旧的 selection
      //      单例 + anchor 选最后一条机制)。深字段 folders 留给 SSE done 后
      //      用 backendMessageId 调接口 5 hydrate 路径补齐。
      dispatch(
        setClassifyPreview({
          sessionId: e.sessionId,
          chainVersion: e.chainVersion,
          expandedSize: e.expandedSize,
          layerCount: e.layerCount,
          layersSummary: e.layersSummary as never
        })
      )
      dispatch(
        assistantPlanSnapshotted({
          messageId: assistantId,
          sessionId: e.sessionId,
          chainVersion: e.chainVersion,
          layersSummary: e.layersSummary as never,
          expandedSize: e.expandedSize,
          layerCount: e.layerCount
        })
      )
    }
  }
}
